import React, { useState } from "react";

import { connect, useSelector } from "react-redux";
import { useFirestoreConnect, isLoaded } from "react-redux-firebase";
import { getSnapshotByObject } from "redux-firestore";

import { useImmer } from "use-immer";

import PropTypes from "prop-types";

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Button,
  createMuiTheme,
  ThemeProvider,
  makeStyles,
} from "@material-ui/core";

import { Alert } from "@material-ui/lab";

import { ReactComponent as SplitwiseIcon } from "../../assets/splitwise_icon.svg";

import splitwise from "../../services/splitwise";

import LoadingComponent from "../../components/LoadingComponent";
import SubscriptionComponent from "../Settings/components/Subscription";

import { updateFlags } from "../../services/firebase";

import TransactionRow from "./components/TransactionRow";

const theme = createMuiTheme({
  overrides: {
    MuiTableCell: {
      root: {
        padding: "6px",
      },
    },
  },
});

const headCells = [
  {
    id: "date",
    numeric: false,
    disablePadding: false,
    label: "Date",
    sortable: true,
  },
  {
    id: "name",
    numeric: false,
    disablePadding: true,
    label: "Transaction",
  },
  { id: "amount", numeric: true, disablePadding: false, label: "Amount" },
];

function EnhancedTableHead(props) {
  const { classes, order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const createTableCell = (headCell) =>
    headCell.sortable ? (
      <TableCell
        key={headCell.id}
        align={headCell.numeric ? "right" : "left"}
        sortDirection={orderBy === headCell.id ? order : false}
      >
        <TableSortLabel
          active={orderBy === headCell.id}
          direction={order}
          onClick={createSortHandler(headCell.id)}
        >
          {headCell.label}
          {orderBy === headCell.id ? (
            <span className={classes.visuallyHidden}>
              {order === "desc" ? "sorted descending" : "sorted ascending"}
            </span>
          ) : null}
        </TableSortLabel>
      </TableCell>
    ) : (
      <TableCell key={headCell.id}>{headCell.label}</TableCell>
    );

  return (
    <TableHead>
      <TableRow>
        {headCells.map(createTableCell)}
        <TableCell></TableCell>
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  tableWrapper: {
    overflowX: "auto",
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

const TransactionTable = ({ auth, splitwiseToken }) => {
  const classes = useStyles();
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("date");
  //const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [paginationArray, updatePaginationArray] = useImmer([null]);

  const collection = `users/${auth.uid}/transactions`;
  useFirestoreConnect({
    collection,
    orderBy: [orderBy, order],
    //where: ["deleted", "==", undefined],
    limit: rowsPerPage,
    startAfter: paginationArray[page],
  });
  const subscriptionStatus = useSelector(
    (state) => state.stripe.subscription && state.stripe.subscription.status
  );
  const flags = useSelector((state) => state.firebase.profile.flags);

  const transactions = useSelector(
    (state) => state.firestore.ordered[collection]
  );
  const handleChangePage = (event, newPage) => {
    const index = transactions.length - 1;
    if (newPage > page) {
      // todo: if redux-firestore fixes the below function this code may need to change
      // this may act a little weird if new transactions come in while user is on page other than 0
      const documentSnapshot = getSnapshotByObject(transactions[index]);
      updatePaginationArray((array) => {
        array[newPage] = documentSnapshot;
      });
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (event, property) => {
    if (orderBy === property) {
      setOrder(order === "desc" ? "asc" : "desc");
    }
    setOrderBy(property);
  };

  const showSplitwiseAlert = !splitwiseToken;
  const showSplitwiseSucccess =
    splitwiseToken && (!flags || !flags.hasDismissedSplitwiseSuccess);
  const handleDismissSplitwiseSuccess = () => {
    updateFlags(auth.uid, { hasDismissedSplitwiseSuccess: true });
  };
  if (!isLoaded(transactions)) return <LoadingComponent />;
  if (transactions.length === 0)
    return (
      <Alert
        severity="success"
        style={{ margin: 8 }}
        variant="outlined"
        action={showSplitwiseAlert && <SplitwiseLinkButton />}
      >
        Account linked. Waiting on transactions to populate.{" "}
        {showSplitwiseAlert ? `Why not link Splitwise while you wait?` : ""}
      </Alert>
    );
  return (
    <ThemeProvider theme={theme}>
      <Paper className={classes.paper}>
        {!showSplitwiseAlert &&
          !showSplitwiseSucccess &&
          subscriptionStatus &&
          subscriptionStatus !== "active" &&
          transactions.length > 0 && (
            <div style={{ padding: "0 8px" }}>
              <SubscriptionComponent />
            </div>
          )}
        {showSplitwiseSucccess && (
          <Alert
            severity="success"
            style={{ margin: 8 }}
            icon={<SplitwiseIcon height={22} />}
            variant="outlined"
            action={
              <Button
                variant="outlined"
                onClick={handleDismissSplitwiseSuccess}
              >
                OK
              </Button>
            }
          >
            Splitwise Successfully linked!
          </Alert>
        )}
        {showSplitwiseAlert && (
          <Alert
            severity="success"
            style={{ margin: 8 }}
            icon={<SplitwiseIcon height={22} />}
            variant="outlined"
            action={<SplitwiseLinkButton />}
          >
            Next, link Splitwise in order to create expenses from your
            transactions.
          </Alert>
        )}
        <div className={classes.tableWrapper}>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={transactions.length}
            />
            <TableBody>
              {transactions.map((row, index) => (
                <TransactionRow transaction={row} key={index} />
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={
              transactions.length < rowsPerPage
                ? page * rowsPerPage + transactions.length
                : rowsPerPage * (page + 2)
            }
            labelDisplayedRows={() => null}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </div>
      </Paper>
    </ThemeProvider>
  );
};

const mapStateToProps = (state) => ({
  auth: state.firebase.auth,
  splitwiseToken: state.firebase.profile.splitwiseToken,
});

export default connect(mapStateToProps)(TransactionTable);

const SplitwiseLinkButton = () => {
  const [loading, setLoading] = useState(false);

  const handleClick = () => {
    setLoading(true);
    window.location = splitwise.getAuthLink();
  };
  return (
    <Button
      variant="outlined"
      component="a"
      onClick={handleClick}
      startIcon={<SplitwiseIcon height={22} />}
    >
      Link
      {loading && <LoadingComponent inner size={22} />}
    </Button>
  );
};
